import { useForm, router, usePage } from "@inertiajs/react"
import { BackofficeLayout } from "@/components/layout/BackofficeLayout"
import { FormSection } from "@/components/backoffice/FormSection"
import { FieldEditor } from "@/components/backoffice/FieldEditor"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import type { JurisdictionOptions, NomineeShareholderTypeSummary, PageProps } from "@/types"

type Field = {
  id: string
  field_type: 'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone' | 'select' | 'multi_select' | 'currency' | 'attachment'
  label: string
  identifier: string
  required: boolean
  encrypted: boolean
  description?: string
  position: number
  options: { id: string; label: string; value: string; position: number }[]
  config: Record<string, unknown>
}

type Props = PageProps & {
  type: NomineeShareholderTypeSummary
  fields: Field[]
  fields_api_path: string
  ordering_api_path: string
  jurisdiction_options: JurisdictionOptions
}

export default function NomineeShareholderTypesEdit() {
  const { type, fields, fields_api_path, ordering_api_path, jurisdiction_options } = usePage<Props>().props
  const { data, setData, processing, errors } = useForm({
    name: type.name,
    description: type.description || "",
    country_id: type.country_id || "",
    state_id: type.state_id || "",
    local_area_id: type.local_area_id || "",
    gov_fee_cents: type.gov_fee_cents,
    service_fee_cents: type.service_fee_cents,
    min_duration_months: type.min_duration_months,
    processing_days_min: type.processing_days_min || 0,
    processing_days_max: type.processing_days_max || 0,
  })

  const selectClass = "flex h-9 w-full items-center rounded-md border border-input bg-transparent pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.patch(`/backoffice/nominee_shareholder_types/${type.id}`, { nominee_shareholder_type: data })
  }

  return (
    <BackofficeLayout
      title={`Edit ${type.name}`}
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Nominee Shareholder Types", href: "/backoffice/nominee_shareholder_types" },
        { label: type.name, href: `/backoffice/nominee_shareholder_types/${type.id}` },
        { label: "Edit" },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Edit {type.name}</h1>
          <p className="text-sm text-muted-foreground font-mono">{type.code}</p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
          <FormSection title="Identification">
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Name</label>
              <Input className="h-8 text-sm" value={data.name} onChange={(e) => setData("name", e.target.value)} />
              {errors.name && <p className="text-[12px] text-destructive">{errors.name}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Description</label>
              <Textarea
                className="text-sm min-h-15"
                value={data.description}
                onChange={(e) => setData("description", e.target.value)}
              />
            </div>
          </FormSection>

          <FormSection title="Jurisdiction">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Country</label>
                <select
                  value={data.country_id}
                  onChange={(e) => setData((d) => ({ ...d, country_id: e.target.value, state_id: "", local_area_id: "" }))}
                  className={selectClass}
                >
                  <option value="">Select...</option>
                  {jurisdiction_options.countries.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">State</label>
                <select
                  value={data.state_id}
                  onChange={(e) => setData((d) => ({ ...d, state_id: e.target.value, local_area_id: "" }))}
                  disabled={!data.country_id}
                  className={selectClass}
                >
                  <option value="">Select...</option>
                  {jurisdiction_options.states
                    .filter((s) => s.country_id === data.country_id)
                    .map((s) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                    ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Local Area</label>
                <select
                  value={data.local_area_id}
                  onChange={(e) => setData("local_area_id", e.target.value)}
                  disabled={!data.state_id}
                  className={selectClass}
                >
                  <option value="">None</option>
                  {jurisdiction_options.local_areas
                    .filter((a) => a.state_id === data.state_id)
                    .map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
                    ))}
                </select>
              </div>
            </div>
          </FormSection>

          <FormSection title="Fees & Duration">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Service Fee (cents)</label>
                <Input
                  className="h-8 text-sm"
                  type="number"
                  value={data.service_fee_cents}
                  onChange={(e) => setData("service_fee_cents", parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Government Fee (cents)</label>
                <Input
                  className="h-8 text-sm"
                  type="number"
                  value={data.gov_fee_cents}
                  onChange={(e) => setData("gov_fee_cents", parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Minimum Duration (months)</label>
              <Input
                className="h-8 text-sm"
                type="number"
                value={data.min_duration_months}
                onChange={(e) => setData("min_duration_months", parseInt(e.target.value) || 12)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Processing Days (min)</label>
                <Input
                  className="h-8 text-sm"
                  type="number"
                  value={data.processing_days_min}
                  onChange={(e) => setData("processing_days_min", parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Processing Days (max)</label>
                <Input
                  className="h-8 text-sm"
                  type="number"
                  value={data.processing_days_max}
                  onChange={(e) => setData("processing_days_max", parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          </FormSection>

          <div className="flex gap-2 pt-2">
            <Button type="submit" size="sm" disabled={processing} className="h-8 text-[13px]">Save Changes</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => window.history.back()} className="h-8 text-[13px]">Cancel</Button>
          </div>
        </form>

        <div className="max-w-2xl">
          <FieldEditor fieldsApiPath={fields_api_path} orderingApiPath={ordering_api_path} fields={fields} />
        </div>
      </div>
    </BackofficeLayout>
  )
}
